/* ==========================================================================
   SAINT THEORY · efectos de navegación y compra
   1. Video en tarjetas del catálogo
   2. Aparición suave, títulos letra por letra, cortinas en campañas
   3. Talla rápida en el catálogo (agrega a la bolsa sin salir)
   4. Producto que vuela a la bolsa + aviso "Agregado"
   5. Intro de marca, cursor premium, cinta que reacciona al scroll, botones magnéticos
   Todo respeta "reducir movimiento" del teléfono y no se activa en el editor de Shopify.
   ========================================================================== */

const stConfig = window.stConfig || {};
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const saveData = navigator.connection && navigator.connection.saveData;
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
const designMode = Boolean(window.Shopify && window.Shopify.designMode);

/* ---------- utilidades de plata ---------- */
function formatMoney(cents) {
  const format = stConfig.moneyFormat || '${{amount_no_decimals_with_comma_separator}}';
  const noDecimals = Math.round(Math.round(cents) / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return format.replace(/\{\{\s*\w+\s*\}\}/, noDecimals).replace(/<[^>]*>/g, '');
}

/* ==========================================================================
   1. Video en tarjetas
   ========================================================================== */
class StCardVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;
    const mode = reduceMotion || saveData ? 'hover' : this.dataset.mode;
    if (mode === 'auto') {
      StCardVideo.observer.observe(this);
    } else {
      const gallery = this.closest('.card-gallery') || this.parentElement;
      gallery.addEventListener('pointerenter', () => this.play());
      gallery.addEventListener('pointerleave', () => this.pause());
    }
  }

  disconnectedCallback() {
    StCardVideo.observer.unobserve(this);
  }

  play() {
    const { video } = this;
    if (!video.src) video.src = video.dataset.src;
    const attempt = video.play();
    if (attempt && attempt.then) attempt.then(() => this.classList.add('is-playing')).catch(() => {});
    else this.classList.add('is-playing');
  }

  pause() {
    this.video.pause();
    this.classList.remove('is-playing');
  }

  static observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) entry.target.play();
        else entry.target.pause();
      }
    },
    { threshold: 0.4 }
  );
}
if (!customElements.get('st-card-video')) customElements.define('st-card-video', StCardVideo);

/* ==========================================================================
   2. Aparición suave + letras + cortinas
   ========================================================================== */
function splitLetters(el) {
  if (el.dataset.stSplit) return;
  el.dataset.stSplit = '1';
  el.classList.add('st-split');
  const text = el.textContent.trim();
  el.setAttribute('aria-label', text);
  el.textContent = '';
  let i = 0;
  text.split(/\s+/).forEach((word, wi, words) => {
    const w = document.createElement('span');
    w.className = 'st-w';
    w.setAttribute('aria-hidden', 'true');
    for (const ch of word) {
      const c = document.createElement('span');
      c.className = 'st-c';
      c.textContent = ch;
      c.style.setProperty('--i', String(i++));
      w.appendChild(c);
    }
    el.appendChild(w);
    if (wi < words.length - 1) el.appendChild(document.createTextNode(' '));
  });
}

function maskHeading(el) {
  if (el.dataset.stMask) return;
  el.dataset.stMask = '1';
  const inner = document.createElement('span');
  while (el.firstChild) inner.appendChild(el.firstChild);
  const mask = document.createElement('span');
  mask.className = 'st-mask';
  mask.appendChild(inner);
  el.appendChild(mask);
}

function stReveal() {
  if (!stConfig.reveal || reduceMotion || designMode || !('IntersectionObserver' in window)) return;
  document.documentElement.classList.add('st-reveal-on');

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );

  const mark = (root) => {
    const visible = (el) => el.getBoundingClientRect().top < window.innerHeight;
    root.querySelectorAll('main .shopify-section > *:not(.st-reveal)').forEach((el) => {
      if (el.closest('.product-information') || el.matches('sticky-add-to-cart, [class*="sticky-add-to-cart"]')) return;
      if (visible(el)) { el.classList.add('st-reveal', 'is-in'); return; }
      el.classList.add('st-reveal');
      observer.observe(el);
    });
    root.querySelectorAll('.product-grid__item:not(.st-reveal)').forEach((el, i) => {
      el.classList.add('st-reveal');
      el.style.setProperty('--st-delay', `${(i % 4) * 0.06}s`);
      observer.observe(el);
    });
    // Títulos grandes de campañas: letra por letra
    root.querySelectorAll('main .hero :is(h1, h2)').forEach((h) => { if (!visible(h)) splitLetters(h); });
    // Campañas con foto (no la primera): cortina
    root
      .querySelectorAll('main .shopify-section:not(:first-of-type) .hero:not(.st-curtain)')
      .forEach((h) => h.classList.add('st-curtain'));
    // Títulos de secciones: suben desde una máscara
    root.querySelectorAll('main .shopify-section :is(h1, h2):not([data-st-mask]):not([data-st-split])').forEach((h) => {
      if (h.closest('.hero, .product-information, .rte, .accordion, .st-lineup, .st-cindex, .st-manifesto, .st-trail, .st-tunnel, .st-fan, .st-lamp')) return;
      if (visible(h)) return;
      maskHeading(h);
    });
  };

  mark(document);
  let pending = false;
  new MutationObserver(() => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      mark(document);
    });
  }).observe(document.querySelector('main') || document.body, { childList: true, subtree: true });

  // Seguro: nada se queda invisible si algo falla
  setTimeout(() => document.querySelectorAll('.st-reveal').forEach((el) => el.classList.add('is-in')), 5000);
}

/* ==========================================================================
   3. Talla rápida
   ========================================================================== */
class StQuickSize extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';
    this.toggle = this.querySelector('.st-qs__toggle');
    this.querySelectorAll('.st-qs__size').forEach((b, i) => b.style.setProperty('--i', String(i)));

    this.toggle?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const open = !this.classList.contains('is-open');
      document.querySelectorAll('st-quick-size.is-open').forEach((el) => el !== this && el.close());
      this.classList.toggle('is-open', open);
      this.toggle.setAttribute('aria-expanded', String(open));
    });

    this.addEventListener('click', (event) => {
      const button = event.target.closest('.st-qs__size');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      if (button.disabled || button.classList.contains('is-loading')) return;
      this.add(button);
    });
  }

  close() {
    this.classList.remove('is-open');
    this.toggle?.setAttribute('aria-expanded', 'false');
  }

  async add(button) {
    button.classList.add('is-loading');
    const image = this.closest('.card-gallery')?.querySelector('img');
    const fromRect = (image || this).getBoundingClientRect();
    const flight = stFlyToBag(fromRect, this.dataset.image || image?.currentSrc);

    try {
      await stAddToCart(button.dataset.variantId, this);
      button.classList.remove('is-loading');
      button.classList.add('is-added');
      await flight;
      setTimeout(() => {
        button.classList.remove('is-added');
        this.close();
      }, 900);
    } catch (error) {
      button.classList.remove('is-loading');
      stToastError(error.message);
    }
  }
}
if (!customElements.get('st-quick-size')) customElements.define('st-quick-size', StQuickSize);

// Cerrar paneles abiertos al tocar fuera (celular)
document.addEventListener('click', (event) => {
  if (event.target.closest('st-quick-size')) return;
  document.querySelectorAll('st-quick-size.is-open').forEach((el) => el.close());
});

/**
 * Agrega una variante a la bolsa por el mismo camino que usa el tema, así la bolsa,
 * el contador y la barra de envío gratis se actualizan solos.
 */
async function stAddToCart(variantId, source, items = [{ id: variantId, quantity: 1 }]) {
  const { CartLinesUpdateEvent } = await import('@shopify/events');

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  const body = { items };
  if (sectionIds.length) body.sections = sectionIds.join(',');

  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    }).then((r) => r.json());

    const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'st-quick-size',
        itemCount: items.length,
        sections: response.sections,
        didError: Boolean(response.status),
      },
    });

    if (response.status) throw new Error(response.description || response.message || 'No se pudo agregar');
    navigator.vibrate?.(12);
    return cart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/**
 * Combo conjunto (snippet st-combo). En la página del conjunto reemplaza el selector y el botón
 * del tema: el cliente elige conjunto completo o una sola pieza, con una talla por pieza, y se
 * agregan las piezas sueltas (el descuento automático de Shopify pone el precio del combo).
 */
class StCombo extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => this.onClick(event));
    if (this.dataset.mode !== 'set') return;
    const info = this.closest('.product-information, product-information, section') || document;
    const buy = info.querySelector('.buy-buttons-block');
    info.querySelectorAll('variant-picker, .buy-buttons-block').forEach((el) => (el.hidden = true));
    buy?.before(this);
    this.pick = 'both';
    this.chosen = {};
    this.render();
  }

  onClick(event) {
    const tab = event.target.closest('[data-pick]');
    const size = event.target.closest('.st-combo__size');
    const add = event.target.closest('.st-combo__add');
    if (tab) {
      this.pick = tab.dataset.pick;
      this.render();
    } else if (size && !size.disabled) {
      if (this.dataset.mode === 'piece') return this.addMate(size);
      const part = size.closest('[data-part]');
      part.querySelectorAll('.st-combo__size').forEach((b) => b.classList.toggle('is-on', b === size));
      this.chosen[part.dataset.part] = size;
      this.render();
    } else if (add && !add.disabled) {
      this.addSet(add);
    }
  }

  parts() {
    return this.pick === 'both' ? ['top', 'bot'] : [this.pick];
  }

  render() {
    const money = (cents) => '$' + Math.round(cents / 100).toLocaleString('es-CO');
    const prices = { top: +this.dataset.topPrice, bot: +this.dataset.botPrice };
    const both = this.pick === 'both';
    const full = this.parts().reduce((sum, part) => sum + prices[part], 0);
    const now = both ? full - +this.dataset.off : full;

    this.querySelectorAll('[data-pick]').forEach((b) => b.setAttribute('aria-checked', b.dataset.pick === this.pick));
    this.querySelectorAll('[data-part]').forEach((el) => {
      el.hidden = !this.parts().includes(el.dataset.part);
      el.querySelector('[data-chosen]').textContent = this.chosen[el.dataset.part]?.dataset.size || '';
    });
    this.querySelector('[data-was]').hidden = !both;
    this.querySelector('[data-save]').hidden = !both;
    this.querySelector('[data-was]').textContent = money(full);
    this.querySelector('[data-now]').textContent = money(now);

    const missing = this.parts().filter((part) => !this.chosen[part]);
    const button = this.querySelector('.st-combo__add');
    button.disabled = missing.length > 0;
    this.querySelector('[data-cta]').textContent = missing.length
      ? both && missing.length === 2
        ? 'Elige tus dos tallas'
        : `Elige la talla de ${missing[0] === 'top' ? 'arriba' : 'la sudadera'}`
      : `${both ? 'Agregar conjunto' : 'Agregar al carrito'} · ${money(now)}`;
  }

  async addSet(button) {
    const properties = this.pick === 'both' ? { Conjunto: `${this.dataset.set} · combo` } : undefined;
    const items = this.parts().map((part) => ({ id: +this.chosen[part].dataset.variantId, quantity: 1, properties }));
    await this.run(button, items, this.dataset.image);
  }

  async addMate(size) {
    const mate = size.closest('.st-combo__mate');
    await this.run(size, [{ id: +size.dataset.variantId, quantity: 1 }], mate.dataset.image);
  }

  async run(button, items, image) {
    if (button.classList.contains('is-loading')) return;
    button.classList.add('is-loading');
    const flight = stFlyToBag(button.getBoundingClientRect(), image);
    try {
      await stAddToCart(items[0].id, this, items);
      await flight;
      button.classList.add('is-added');
      setTimeout(() => button.classList.remove('is-added'), 1200);
    } catch (error) {
      stToastError(error.message);
    } finally {
      button.classList.remove('is-loading');
    }
  }
}
if (!customElements.get('st-combo')) customElements.define('st-combo', StCombo);

/**
 * Modo app en celular (snippet st-tabbar): menú fijo abajo. Buscar y Carrito usan los botones del
 * encabezado (escondidos en celular); el número del carrito se copia del globito del encabezado cada vez que cambia.
 */
class StTabbar extends HTMLElement {
  connectedCallback() {
    this.sheet = this.querySelector('.st-tabbar__sheet');
    this.panel = this.querySelector('.st-tabbar__panel');
    this.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-tab]');
      if (event.target.closest('[data-close]')) return this.close();
      if (!tab) return;
      navigator.vibrate?.(8);
      if (tab.dataset.tab === 'sheet') this.open();
      if (tab.dataset.tab === 'search') {
        this.close();
        document.querySelector('.search-action button')?.click();
      }
      if (tab.dataset.tab === 'cart') {
        const trigger = document.querySelector('[data-testid="cart-drawer-trigger"], .header-actions__action.action__cart');
        trigger ? trigger.click() : (location.href = (window.Shopify?.routes?.root || '/') + 'cart');
      }
    });
    document.addEventListener('keydown', (event) => event.key === 'Escape' && this.close());
    this.swipeToClose();
    this.syncCount();
  }

  open() {
    this.sheet.hidden = false;
    requestAnimationFrame(() => this.sheet.classList.add('is-open'));
  }

  close() {
    if (this.sheet.hidden) return;
    this.sheet.classList.remove('is-open');
    this.panel.style.transform = '';
    setTimeout(() => (this.sheet.hidden = true), 350);
  }

  swipeToClose() {
    let startY = null;
    this.panel.addEventListener('touchstart', (e) => (startY = e.touches[0].clientY), { passive: true });
    this.panel.addEventListener('touchmove', (e) => {
      const dy = e.touches[0].clientY - startY;
      if (startY !== null && dy > 0) this.panel.style.transform = `translateY(${dy}px)`;
    }, { passive: true });
    this.panel.addEventListener('touchend', (e) => {
      const dy = e.changedTouches[0].clientY - startY;
      startY = null;
      dy > 80 ? this.close() : (this.panel.style.transform = '');
    });
  }

  syncCount() {
    const badge = this.querySelector('[data-count]');
    const source = document.querySelector('.header-actions__cart-icon');
    if (!source) return;
    const copy = () => {
      const n = parseInt(source.querySelector('.cart-bubble__text-count')?.textContent, 10) || 0;
      badge.textContent = n;
      badge.hidden = n === 0;
    };
    new MutationObserver(copy).observe(source, { subtree: true, childList: true, characterData: true });
  }
}
if (!customElements.get('st-tabbar')) customElements.define('st-tabbar', StTabbar);

/* ==========================================================================
   4. Vuelo a la bolsa + aviso
   ========================================================================== */
function cartTarget() {
  return (
    [...document.querySelectorAll('.header-actions__cart-icon, .action__cart, .st-tabbar [data-tab="cart"]')].find((el) => {
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    }) || null
  );
}

function stBumpBag() {
  const bag = cartTarget();
  if (!bag) return;
  bag.classList.remove('st-bump');
  void bag.offsetWidth;
  bag.classList.add('st-bump');
  const r = bag.getBoundingClientRect();
  const ring = document.createElement('span');
  ring.className = 'st-ring';
  ring.style.left = `${r.left + r.width / 2}px`;
  ring.style.top = `${r.top + r.height / 2}px`;
  document.body.appendChild(ring);
  setTimeout(() => ring.remove(), 900);
}

function stFlyToBag(fromRect, imageUrl) {
  const bag = cartTarget();
  if (!bag || !imageUrl || reduceMotion) {
    stBumpBag();
    return Promise.resolve();
  }
  const DURATION = 2600;
  const w = Math.max(140, Math.min(fromRect.width, 230));
  const h = w * (fromRect.height / fromRect.width || 4 / 3);
  const start = { x: fromRect.left + fromRect.width / 2, y: fromRect.top + fromRect.height / 2 };
  const to = bag.getBoundingClientRect();
  const end = { x: to.left + to.width / 2, y: to.top + to.height / 2 };
  // Punto donde la prenda "espera" justo debajo de la bolsa (siempre dentro de la pantalla)
  const hover = { x: end.x, y: end.y + 110 };

  const fly = document.createElement('div');
  fly.className = 'st-fly';
  fly.style.width = `${w}px`;
  fly.style.height = `${h}px`;
  fly.style.backgroundImage = `url("${imageUrl}")`;
  document.body.appendChild(fly);

  const ease = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
  const easeOut = (x) => 1 - Math.pow(1 - x, 3);
  const lerp = (a, b, k) => a + (b - a) * k;
  const at = (x, y, s, r) => `translate(${x - w / 2}px, ${y - h / 2}px) scale(${s}) rotate(${r}deg)`;
  const frames = [];
  const push = (offset, x, y, s, r, o = 1, radius = 4) =>
    frames.push({ offset, transform: at(x, y, s, r), opacity: o, borderRadius: `${radius}px` });

  // 1) Se despega de la foto (0 → 12%)
  const lifted = { x: start.x, y: start.y - 26 };
  for (let i = 0; i <= 4; i++) {
    const k = easeOut(i / 4);
    push((i / 4) * 0.12, lerp(start.x, lifted.x, k), lerp(start.y, lifted.y, k), lerp(1, 1.08, k), lerp(0, -3, k), 1, lerp(0, 4, k));
  }
  // 2) Viaja en curva suave hasta quedar debajo de la bolsa (12% → 66%)
  const ctrl = { x: lerp(lifted.x, hover.x, 0.15), y: Math.max(hover.y + 40, Math.min(lifted.y, hover.y + 220)) };
  for (let i = 1; i <= 24; i++) {
    const k = ease(i / 24);
    const x = (1 - k) * (1 - k) * lifted.x + 2 * (1 - k) * k * ctrl.x + k * k * hover.x;
    const y = (1 - k) * (1 - k) * lifted.y + 2 * (1 - k) * k * ctrl.y + k * k * hover.y;
    push(0.12 + (i / 24) * 0.54, x, y, lerp(1.08, 0.42, k), lerp(-3, 5, k), 1, lerp(4, 6, k));
  }
  // 3) Flota un instante (66% → 76%)
  push(0.71, hover.x, hover.y - 6, 0.43, 2, 1, 6);
  push(0.76, hover.x, hover.y, 0.42, 0, 1, 6);
  // 4) Salta por encima de la bolsa (76% → 88%)
  for (let i = 1; i <= 4; i++) {
    const k = easeOut(i / 4);
    push(0.76 + (i / 4) * 0.12, hover.x, lerp(hover.y, end.y - 30, k), lerp(0.42, 0.3, k), 0, 1, 8);
  }
  // 5) Cae DENTRO de la bolsa (88% → 100%)
  push(0.95, end.x, end.y - 4, 0.14, 0, 1, 10);
  push(1, end.x, end.y + 4, 0.04, 0, 0, 20);

  const animation = fly.animate(frames, { duration: DURATION, easing: 'linear', fill: 'forwards' });

  // Estela suave detrás de la prenda
  const trail = setInterval(() => {
    const m = new DOMMatrix(getComputedStyle(fly).transform);
    const dot = document.createElement('span');
    dot.className = 'st-fly-trail';
    dot.style.left = `${m.m41 + w / 2}px`;
    dot.style.top = `${m.m42 + h / 2}px`;
    document.body.appendChild(dot);
    dot
      .animate([{ opacity: 0.25, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(0.2)' }], {
        duration: 700,
        fill: 'forwards',
      })
      .finished.then(() => dot.remove());
  }, 70);

  // La bolsa se abre cuando la prenda salta, y se cierra al recibirla
  setTimeout(() => bag.classList.add('st-bag-open'), DURATION * 0.74);

  return animation.finished.then(() => {
    clearInterval(trail);
    fly.remove();
    bag.classList.remove('st-bag-open');
    stBumpBag();
  });
}

/** Foto principal visible en la página de producto (para que vuele desde ahí) */
function productHeroImage() {
  const imgs = [...document.querySelectorAll('.product-information__media img, .product-media-container img, .product-information img')];
  return (
    imgs.find((img) => {
      const r = img.getBoundingClientRect();
      return r.width > 80 && r.bottom > 0 && r.top < window.innerHeight;
    }) || imgs[0]
  );
}

let toastEl;
let toastTimer;
function toastShell() {
  if (toastEl) return toastEl;
  toastEl = document.createElement('div');
  toastEl.className = 'st-toast';
  toastEl.setAttribute('role', 'status');
  toastEl.setAttribute('aria-live', 'polite');
  document.body.appendChild(toastEl);
  toastEl.addEventListener('pointerenter', () => clearTimeout(toastTimer));
  toastEl.addEventListener('pointerleave', () => hideToastLater());
  return toastEl;
}
function hideToastLater(ms = 4500) {
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toastEl?.classList.remove('is-visible'), ms);
}

function openCartDrawer() {
  toastEl?.classList.remove('is-visible');
  const trigger = document.querySelector('.action__cart');
  if (trigger) trigger.click();
  else window.location.href = '/cart';
}

function stToast(item, cart) {
  if (!stConfig.toast || !item) return;
  const el = toastShell();
  const goal = Number(stConfig.freeShipping || 0) * 100;
  const missing = goal - cart.total_price;
  const pct = goal ? Math.min(100, (cart.total_price / goal) * 100) : 0;
  const img = item.featured_image?.url || item.image || '';
  const size = item.variant_title && item.variant_title !== 'Default Title' ? `Talla ${item.variant_title} · ` : '';

  el.innerHTML = `
    <div class="st-toast__img"></div>
    <div>
      <div class="st-toast__eyebrow">
        <svg class="st-toast__check" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2 8.5l3.5 3.5L14 4" stroke="#fff" stroke-width="1.4"/></svg>
        Agregado a tu carrito
      </div>
      <p class="st-toast__title"></p>
      <div class="st-toast__meta"></div>
    </div>
    ${
      goal
        ? `<div class="st-toast__ship"><span class="st-toast__ship-text"></span><div class="st-toast__ship-bar"><span></span></div></div>`
        : ''
    }
    <div class="st-toast__actions">
      <button type="button" data-st-open-cart></button>
      <a href="/checkout">Pagar</a>
    </div>`;
  el.querySelector('.st-toast__img').style.backgroundImage = img ? `url("${img}")` : '';
  el.querySelector('.st-toast__title').textContent = item.product_title;
  el.querySelector('.st-toast__meta').textContent = `${size}${formatMoney(item.final_price)}`;
  el.querySelector('[data-st-open-cart]').textContent = `Ver carrito (${cart.item_count})`;
  const shipText = el.querySelector('.st-toast__ship-text');
  if (shipText)
    shipText.textContent = missing > 0 ? `Te faltan ${formatMoney(missing)} para el envío gratis` : 'Tienes envío gratis';
  el.querySelector('[data-st-open-cart]').addEventListener('click', openCartDrawer);

  el.classList.remove('is-visible');
  void el.offsetWidth;
  el.classList.add('is-visible');
  requestAnimationFrame(() => {
    const bar = el.querySelector('.st-toast__ship-bar span');
    if (bar) bar.style.width = `${pct}%`;
  });
  hideToastLater();
}

function stToastError(message) {
  const el = toastShell();
  el.innerHTML = '<div style="grid-column:1/-1;font-size:.75rem;letter-spacing:.1em;text-transform:uppercase"></div>';
  el.firstElementChild.textContent = message || 'No se pudo agregar. Intenta de nuevo.';
  el.classList.add('is-visible');
  hideToastLater(3500);
}

// Escucha TODAS las compras (catálogo, página de producto, barra fija) para el aviso y el rebote
async function stListenCart() {
  let StandardEvents;
  try {
    ({ StandardEvents } = await import('@shopify/events'));
  } catch (e) {
    return;
  }
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (event.action !== 'add' || !event.promise) return;
    const variantId = String(event.lines?.[0]?.merchandiseId || '').replace(/\D/g, '');
    const fromQuickSize = Boolean(event.target?.closest?.('st-quick-size'));
    const clickedAt = performance.now();

    // Compra desde la página de producto (o barra fija): la foto grande vuela a la bolsa
    let flight = Promise.resolve();
    if (!fromQuickSize) {
      const img = productHeroImage();
      if (img) flight = stFlyToBag(img.getBoundingClientRect(), img.currentSrc || img.src);
      else stBumpBag();
    }

    event.promise
      .then(async ({ detail }) => {
        if (detail?.didError) return;
        const root = window.Shopify?.routes?.root || '/';
        const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        const item = cart.items.find((it) => String(it.variant_id) === variantId) || cart.items[0];
        // El aviso con "Pagar" no espera a que termine la animación completa
        const wait = Math.max(0, 1300 - (performance.now() - clickedAt));
        await Promise.race([flight, new Promise((r) => setTimeout(r, wait))]);
        stToast(item, cart);
      })
      .catch(() => {});
  });
}

/* ==========================================================================
   5. Intro, cursor, cinta, botones magnéticos
   ========================================================================== */
function stIntro() {
  const root = document.documentElement;
  if (!root.classList.contains('st-intro-play')) return;
  const intro = document.querySelector('.st-intro');
  const finish = () => {
    root.classList.add('st-intro-done');
  };
  intro?.addEventListener('click', finish, { once: true });
  intro?.addEventListener('animationend', (e) => {
    if (e.animationName === 'st-intro-out') finish();
  });
  setTimeout(finish, 4500);
}

function stCursor() {
  if (!stConfig.cursor || !finePointer || reduceMotion || designMode) return;
  const cursor = document.createElement('div');
  cursor.className = 'st-cursor';
  cursor.setAttribute('aria-hidden', 'true');
  cursor.innerHTML = '<span class="st-cursor__label"></span>';
  document.body.appendChild(cursor);
  const label = cursor.firstElementChild;

  let x = -100;
  let y = -100;
  let cx = x;
  let cy = y;
  let raf = null;
  const loop = () => {
    cx += (x - cx) * 0.22;
    cy += (y - cy) * 0.22;
    cursor.style.translate = `${cx}px ${cy}px`;
    raf = Math.abs(x - cx) + Math.abs(y - cy) > 0.3 ? requestAnimationFrame(loop) : null;
  };

  document.addEventListener('pointermove', (e) => {
    x = e.clientX;
    y = e.clientY;
    if (!raf) raf = requestAnimationFrame(loop);
    cursor.classList.add('is-active');
    const t = e.target instanceof Element ? e.target : document.body;
    const onQuick = t.closest('st-quick-size');
    const card = !onQuick && t.closest('.product-card .card-gallery, .resource-list__item .card-gallery');
    const hero = !card && t.closest('.hero__link');
    const link = t.closest('a, button, [role="button"], label, summary');
    let text = '';
    if (card) text = 'Ver';
    else if (hero) text = 'Descubrir';
    cursor.classList.toggle('is-view', Boolean(text));
    cursor.classList.toggle('is-link', !text && Boolean(link));
    if (text) label.textContent = text;
  });
  document.addEventListener('pointerdown', () => cursor.classList.add('is-down'));
  document.addEventListener('pointerup', () => cursor.classList.remove('is-down'));
  document.documentElement.addEventListener('pointerleave', () => cursor.classList.remove('is-active'));
}

function stMarqueeSkew() {
  if (reduceMotion) return;
  let last = null;
  let lastT = 0;
  let skew = 0;
  let raf;
  const settle = () => {
    skew *= 0.85;
    document.querySelectorAll('.marquee__content').forEach((el) => el.style.setProperty('--st-skew', `${skew.toFixed(2)}deg`));
    if (Math.abs(skew) > 0.05) raf = requestAnimationFrame(settle);
  };
  document.addEventListener(
    'scroll',
    (e) => {
      const scroller = e.target === document ? document.scrollingElement : e.target;
      if (!scroller || typeof scroller.scrollTop !== 'number') return;
      const now = performance.now();
      if (last !== null) {
        const v = (scroller.scrollTop - last) / Math.max(16, now - lastT);
        skew = Math.max(-10, Math.min(10, -v * 4));
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(settle);
      }
      last = scroller.scrollTop;
      lastT = now;
    },
    { capture: true, passive: true }
  );
}

function stMagnetic() {
  if (!finePointer || reduceMotion || designMode) return;
  document.addEventListener('pointermove', (e) => {
    const t = e.target instanceof Element ? e.target : null;
    const btn = t && t.closest('.hero .button, .hero .button-secondary, .st-toast__actions > *');
    document.querySelectorAll('[data-st-mag]').forEach((el) => {
      if (el !== btn) {
        el.style.transform = '';
        el.removeAttribute('data-st-mag');
      }
    });
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const dx = (e.clientX - (r.left + r.width / 2)) * 0.25;
    const dy = (e.clientY - (r.top + r.height / 2)) * 0.35;
    btn.setAttribute('data-st-mag', '');
    btn.style.transition = 'transform .25s cubic-bezier(.22,1,.36,1)';
    btn.style.transform = `translate(${dx}px, ${dy}px)`;
  });
}

/* ==========================================================================
   6. Registro con descuento ("Únete al gesto")
   ========================================================================== */
function stWelcome() {
  const box = document.querySelector('[data-st-welcome]');
  const tab = document.querySelector('[data-st-welcome-open]');
  if (!box || designMode) return;
  const KEY_DONE = 'st-welcome-done';
  const KEY_SEEN = 'st-welcome-seen';
  // localStorage + cookie de respaldo (algunos navegadores dentro de Instagram/Facebook borran el almacenamiento)
  const store = {
    get: (k) => {
      let v = null;
      try {
        v = localStorage.getItem(k);
      } catch (e) {}
      if (v) return v;
      const m = document.cookie.match(new RegExp('(?:^|; )' + k + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    },
    set: (k, v) => {
      try {
        localStorage.setItem(k, v);
      } catch (e) {}
      document.cookie = `${k}=${encodeURIComponent(v)}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
    },
  };

  const open = () => {
    if (box.classList.contains('is-open')) return;
    store.set(KEY_SEEN, String(Date.now())); // vista = mostrada una vez, sin importar cómo se cierre
    box.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => box.classList.add('is-open')));
    tab?.classList.remove('is-visible');
    setTimeout(() => box.querySelector('input[type="email"]')?.focus({ preventScroll: true }), 900);
  };
  const close = () => {
    box.classList.remove('is-open');
    store.set(KEY_SEEN, String(Date.now()));
    setTimeout(() => {
      box.hidden = true;
    }, 600);
    if (!store.get(KEY_DONE) && tab) {
      tab.hidden = false;
      requestAnimationFrame(() => tab.classList.add('is-visible'));
    }
  };

  box.querySelectorAll('[data-st-welcome-close]').forEach((el) => el.addEventListener('click', close));
  document.addEventListener('keydown', (e) => e.key === 'Escape' && box.classList.contains('is-open') && close());
  tab?.addEventListener('click', open);

  // Copiar el código
  box.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-st-copy]');
    if (!btn) return;
    navigator.clipboard?.writeText(btn.dataset.stCopy).then(() => {
      const label = btn.querySelector('[data-st-copy-label]');
      if (label) label.textContent = '¡Copiado!';
    });
  });

  // Anuncio "Regístrate..." abre la ventana
  document.querySelectorAll('.announcement-bar__text, [class*="announcement"] p, [class*="announcement"] span').forEach((el) => {
    if (/Reg[íi]strate/i.test(el.textContent) && !el.querySelector('*')) {
      el.setAttribute('data-st-welcome-link', '');
      el.addEventListener('click', (e) => {
        e.preventDefault();
        open();
      });
    }
  });

  // Recién registrado (desde la ventana o desde el pie de página): mostrar el código
  const justJoined = box.querySelector('[data-st-welcome-success]') || /customer_posted=true/.test(location.search);
  if (justJoined) {
    store.set(KEY_DONE, '1');
    if (!box.querySelector('[data-st-welcome-success]')) return; // registro del pie: Shopify ya muestra su mensaje
    open();
    return;
  }
  if (store.get(KEY_DONE)) return;

  // Quien llega de un anuncio o está en un producto / la bolsa va a comprar: no lo interrumpimos.
  // Solo queda la pestaña "15% OFF" al lado para quien la quiera.
  let fromAd = false;
  try {
    fromAd = sessionStorage.getItem('st-ad') === '1';
  } catch (e) {}
  const buying = /\/(products|cart|checkout)/.test(location.pathname);
  if (fromAd || buying) {
    if (tab) {
      tab.hidden = false;
      requestAnimationFrame(() => tab.classList.add('is-visible'));
    }
    return;
  }

  const seen = Number(store.get(KEY_SEEN) || 0);
  const days = Number(stConfig.welcomeDays || 30);
  if (seen && Date.now() - seen < days * 864e5) {
    if (tab) {
      tab.hidden = false;
      requestAnimationFrame(() => tab.classList.add('is-visible'));
    }
    return;
  }

  // Aparece después de unos segundos (y nunca encima de la intro)
  const delay = Number(box.dataset.delay || 7) * 1000;
  const introExtra = document.documentElement.classList.contains('st-intro-play') ? 1500 : 0;
  // Aparece cuando la persona ya está explorando (bajó un poco) — o como respaldo a los 20 s
  let armed = false;
  setTimeout(() => {
    armed = true;
  }, delay + introExtra);
  const onScroll = () => {
    const sc = document.querySelector('.page-wrapper');
    const y = Math.max(window.scrollY, sc ? sc.scrollTop : 0);
    if (armed && y > window.innerHeight * 0.6) {
      document.removeEventListener('scroll', onScroll, true);
      open();
    }
  };
  document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  // En computador: si el mouse se va hacia arriba para salir, la mostramos antes
  if (finePointer) {
    const onExit = (e) => {
      if (e.relatedTarget || e.clientY > 10) return;
      document.removeEventListener('mouseout', onExit);
      open();
    };
    document.addEventListener('mouseout', onExit);
  }
}

/* ==========================================================================
   7. THE LINEUP — fila de modelos
   ========================================================================== */
class StLineup extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';
    this.stage = this.querySelector('[data-st-lineup-stage]');
    this.figures = [...this.querySelectorAll('.st-lineup__figure')];
    this.titleEl = this.querySelector('[data-st-lineup-title]');
    this.metaEl = this.querySelector('[data-st-lineup-meta]');
    this.cta = this.querySelector('[data-st-lineup-cta]');
    this.indexEl = this.querySelector('[data-st-lineup-index]');
    this.defaults = {
      title: this.titleEl?.textContent,
      meta: this.metaEl?.textContent,
      cta: this.cta?.textContent,
      href: this.cta?.getAttribute('href'),
    };
    this.active = null;
    if (!this.figures.length) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.classList.add('is-in');
        setTimeout(() => this.classList.add('is-ready'), 1400);
        io.disconnect();
        if (this.isMobile()) this.syncMobile();
      },
      { threshold: 0.2 }
    );
    io.observe(this);
    if (reduceMotion || designMode) this.classList.add('is-in', 'is-ready');

    // Selección "con intención": el modelo elegido se queda quieto mientras vas a darle clic.
    // Solo cambia si el mouse se QUEDA un momento sobre el lugar de otro modelo.
    this.stage.addEventListener('pointermove', (e) => {
      if (this.isMobile() || e.pointerType === 'touch') return;
      clearTimeout(this.leaveTimer);
      const overFigure = e.target.closest('.st-lineup__figure');
      if (this.active && overFigure === this.active) {
        this.cancelPending();
        return;
      }
      const candidate = this.slotAt(e.clientX);
      if (!this.active) {
        this.activate(candidate);
        return;
      }
      if (candidate === this.active) {
        this.cancelPending();
        return;
      }
      if (candidate === this.pending) return;
      this.cancelPending();
      this.pending = candidate;
      this.pendingTimer = setTimeout(() => {
        this.pending = null;
        this.activate(candidate);
      }, 420);
    });
    // Al salir de la sección (o ir al botón "Ver look" de arriba) el look elegido NO se borra de una
    this.addEventListener('pointerleave', () => {
      if (this.isMobile()) return;
      this.cancelPending();
      clearTimeout(this.leaveTimer);
      this.leaveTimer = setTimeout(() => this.deactivate(), 2500);
    });
    this.addEventListener('pointerenter', () => clearTimeout(this.leaveTimer));
    this.querySelector('.st-lineup__head')?.addEventListener('pointermove', () => this.cancelPending());
    this.stage.addEventListener('click', (e) => {
      if (this.isMobile()) {
        const fig = e.target.closest('.st-lineup__figure');
        if (fig && fig !== this.active) {
          e.preventDefault();
          fig.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
        return;
      }
      if (this.active) {
        e.preventDefault();
        window.location.href = this.active.href;
      }
    });

    let raf;
    this.stage.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.syncMobile());
      },
      { passive: true }
    );
    window.addEventListener('resize', () => this.isMobile() ? this.syncMobile() : this.deactivate());
  }

  isMobile() {
    return window.innerWidth < 990;
  }

  cancelPending() {
    clearTimeout(this.pendingTimer);
    this.pending = null;
  }

  /** Qué modelo está bajo el mouse según su lugar ORIGINAL en la fila (así no "salta") */
  slotAt(clientX) {
    const stageRect = this.stage.getBoundingClientRect();
    const x = clientX - stageRect.left;
    let best = null;
    let bestDist = Infinity;
    for (const fig of this.figures) {
      const center = fig.offsetLeft + fig.offsetWidth / 2;
      const d = Math.abs(center - x);
      if (d < bestDist) {
        bestDist = d;
        best = fig;
      }
    }
    return best;
  }

  activate(fig) {
    if (!fig || fig === this.active) return;
    this.active = fig;
    this.classList.add('has-active');
    const stageCenter = this.stage.clientWidth / 2;
    for (const f of this.figures) {
      const on = f === fig;
      f.classList.toggle('is-active', on);
      const dx = on && !this.isMobile() ? stageCenter - (f.offsetLeft + f.offsetWidth / 2) : 0;
      f.style.setProperty('--dx', `${dx}px`);
    }
    this.updateInfo(fig);
  }

  deactivate() {
    this.active = null;
    this.classList.remove('has-active');
    this.figures.forEach((f) => {
      f.classList.remove('is-active');
      f.style.setProperty('--dx', '0px');
    });
    this.updateInfo(null);
  }

  syncMobile() {
    if (!this.isMobile()) return;
    const mid = this.stage.scrollLeft + this.stage.clientWidth / 2;
    let best = this.figures[0];
    let bestDist = Infinity;
    for (const f of this.figures) {
      const d = Math.abs(f.offsetLeft + f.offsetWidth / 2 - mid);
      if (d < bestDist) {
        bestDist = d;
        best = f;
      }
    }
    this.activate(best);
  }

  updateInfo(fig) {
    this.classList.add('is-swapping');
    clearTimeout(this.swapTimer);
    this.swapTimer = setTimeout(() => {
      if (fig) {
        this.titleEl.textContent = fig.dataset.name;
        this.metaEl.textContent = [fig.dataset.title, fig.dataset.price].filter(Boolean).join(' · ');
        this.cta.textContent = 'Ver look';
        this.cta.href = fig.href;
        if (this.indexEl) this.indexEl.textContent = fig.dataset.index;
      } else {
        this.titleEl.textContent = this.defaults.title;
        this.metaEl.textContent = this.defaults.meta;
        this.cta.textContent = this.defaults.cta;
        this.cta.href = this.defaults.href;
        if (this.indexEl) this.indexEl.textContent = '00';
      }
      this.classList.remove('is-swapping');
    }, 160);
  }
}
if (!customElements.get('st-lineup')) customElements.define('st-lineup', StLineup);

/**
 * Prueba el look (sección st-look). La fila se desliza con el dedo o arrastrando; el más cercano
 * al centro manda en el panel. Solo cambia una variable por modelo (tamaño + opacidad), sin filtros.
 */
class StLook extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';
    this.track = this.querySelector('[data-track]');
    this.figs = [...this.querySelectorAll('.st-look__fig')];
    this.sheets = [...this.querySelectorAll('[data-sheet]')];
    this.ticks = [...this.querySelectorAll('[data-tick]')];
    this.countEl = this.querySelector('[data-count]');
    if (!this.figs.length) return;

    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => this.sync());
    };
    this.track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    this.drag();
    this.addEventListener('click', (e) => this.onClick(e));
    this.sheets.forEach((sheet) => this.refresh(sheet));
    this.sync();
  }

  center(fig) {
    return fig.offsetLeft + fig.offsetWidth / 2;
  }

  sync() {
    const mid = this.track.scrollLeft + this.track.clientWidth / 2;
    const reach = (this.figs[0].offsetWidth || 1) * 1.7;
    let best = 0;
    let bestDist = Infinity;
    this.figs.forEach((fig, i) => {
      const dist = Math.abs(this.center(fig) - mid);
      fig.style.setProperty('--k', Math.max(0, 1 - dist / reach).toFixed(3));
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    if (best !== this.current) this.select(best);
  }

  select(i) {
    this.current = i;
    this.figs.forEach((fig, n) => fig.classList.toggle('is-active', n === i));
    this.sheets.forEach((sheet, n) => (sheet.hidden = n !== i));
    this.ticks.forEach((tick, n) => tick.classList.toggle('is-on', n === i));
    if (this.countEl) this.countEl.textContent = String(i + 1).padStart(2, '0');
  }

  goTo(i) {
    const fig = this.figs[Math.max(0, Math.min(this.figs.length - 1, i))];
    this.track.scrollTo({
      left: this.center(fig) - this.track.clientWidth / 2,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  /** Arrastrar con el mouse (en celular ya se desliza con el dedo). */
  drag() {
    let startX = 0;
    let startLeft = 0;
    let down = false;
    this.moved = false;
    this.track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true;
      this.moved = false;
      startX = e.clientX;
      startLeft = this.track.scrollLeft;
    });
    window.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 5) {
        this.moved = true;
        this.track.classList.add('is-dragging');
      }
      if (this.moved) this.track.scrollLeft = startLeft - dx;
    });
    window.addEventListener('pointerup', () => {
      if (!down) return;
      down = false;
      if (this.moved) {
        this.track.classList.remove('is-dragging');
        this.goTo(this.current);
      }
    });
  }

  onClick(e) {
    const go = e.target.closest('[data-go]');
    const tick = e.target.closest('[data-tick]');
    const pin = e.target.closest('[data-pin]');
    const fig = e.target.closest('.st-look__fig');
    const size = e.target.closest('.st-look__size');
    const add = e.target.closest('[data-add]');
    const sheet = e.target.closest('[data-sheet]');
    if (go) return this.goTo(this.current + Number(go.dataset.go));
    if (tick) return this.goTo(Number(tick.dataset.tick));
    if (pin) {
      e.preventDefault();
      const piece = this.sheets[this.current].querySelector('[data-piece]');
      piece?.classList.add('is-flash');
      setTimeout(() => piece?.classList.remove('is-flash'), 900);
      if (window.innerWidth < 990) piece?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (fig) {
      // Tocar a uno de los lados lo trae al centro; arrastrar nunca abre el producto.
      const i = Number(fig.dataset.i);
      if (this.moved || i !== this.current) {
        e.preventDefault();
        if (!this.moved) this.goTo(i);
      }
      this.moved = false;
      return;
    }
    if (size && !size.disabled) {
      size.parentElement.querySelectorAll('.st-look__size').forEach((b) => b.classList.toggle('is-on', b === size));
      this.refresh(sheet);
      return;
    }
    if (add && !add.disabled) return this.addLook(sheet, add);
    if (e.target.matches('.st-look__piece--extra input')) this.refresh(sheet);
  }

  refresh(sheet) {
    const money = (cents) => '$' + Math.round(cents / 100).toLocaleString('es-CO');
    const piece = sheet.querySelector('[data-piece]');
    const extra = sheet.querySelector('[data-extra]');
    const withExtra = extra?.querySelector('input').checked;
    const total = Number(piece?.dataset.price || 0) + (withExtra ? Number(extra.dataset.price) : 0);
    const button = sheet.querySelector('[data-add]');
    const chosen = piece?.querySelector('.st-look__size.is-on');
    button.disabled = !chosen;
    button.textContent = chosen ? `${withExtra ? 'Agregar look' : 'Agregar al carrito'} · ${money(total)}` : 'Elige tu talla';
  }

  async addLook(sheet, button) {
    if (button.classList.contains('is-loading')) return;
    const items = [{ id: Number(sheet.querySelector('.st-look__size.is-on').dataset.variantId), quantity: 1 }];
    const extra = sheet.querySelector('[data-extra]');
    if (extra?.querySelector('input').checked) items.push({ id: Number(extra.dataset.variantId), quantity: 1 });
    button.classList.add('is-loading');
    const img = this.figs[this.current].querySelector('img');
    const flight = stFlyToBag(img.getBoundingClientRect(), img.currentSrc || img.src);
    try {
      await stAddToCart(items[0].id, this, items);
      await flight;
      button.classList.add('is-added');
      setTimeout(() => button.classList.remove('is-added'), 1200);
    } catch (error) {
      stToastError(error.message);
    } finally {
      button.classList.remove('is-loading');
    }
  }
}
if (!customElements.get('st-look')) customElements.define('st-look', StLook);

/**
 * The Wall (sección st-orbit): sneakers en un anillo que gira. Solo cambia transform/opacity de
 * unas pocas fotos, y el cuadro se recalcula únicamente mientras algo se mueve.
 */
class StOrbit extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';
    this.stage = this.querySelector('[data-stage]');
    this.shoes = [...this.querySelectorAll('.st-orbit__shoe')];
    this.sheets = [...this.querySelectorAll('[data-sheet]')];
    this.ruler = this.querySelector('[data-ruler]');
    this.n = this.shoes.length;
    if (!this.n) return;
    this.pos = 0;
    this.target = 0;
    this.current = -1;
    this.idleUntil = 0;
    this.render();
    this.drag();
    this.addEventListener('click', (e) => this.onClick(e));
    window.addEventListener('resize', () => this.render(), { passive: true });
    if (!reduceMotion) {
      new IntersectionObserver(([en]) => (this.visible = en.isIntersecting), { threshold: 0.35 }).observe(this);
      setInterval(() => {
        if (this.visible && !this.dragging && Date.now() > this.idleUntil && !document.hidden) this.go(this.target + 1);
      }, 4200);
    }
    this.addEventListener('pointerenter', () => (this.idleUntil = Date.now() + 8000));
  }

  wrap(d) {
    const n = this.n;
    return ((((d + n / 2) % n) + n) % n) - n / 2;
  }

  render() {
    const w = this.stage.clientWidth;
    const h = this.stage.clientHeight;
    const rx = w * (w < 990 ? 0.42 : 0.3);
    const ry = h * 0.3;
    this.shoes.forEach((shoe, i) => {
      const t = (this.wrap(i - this.pos) * 2 * Math.PI) / this.n;
      const z = (Math.cos(t) + 1) / 2; // 1 = adelante, 0 = atrás
      shoe.style.setProperty('--x', `${(Math.sin(t) * rx).toFixed(1)}px`);
      shoe.style.setProperty('--y', `${(-(1 - z) * ry).toFixed(1)}px`);
      shoe.style.setProperty('--s', (0.34 + 0.66 * z ** 1.6).toFixed(3));
      shoe.style.setProperty('--r', `${(-Math.sin(t) * 7).toFixed(2)}deg`);
      shoe.style.opacity = (0.16 + 0.84 * z ** 2.2).toFixed(3);
      shoe.style.zIndex = String(Math.round(z * 100));
    });
    this.ruler?.style.setProperty('--bp', `${(-this.pos * 42).toFixed(1)}px`);
    const front = ((Math.round(this.pos) % this.n) + this.n) % this.n;
    if (front !== this.current) this.select(front);
  }

  select(i) {
    this.current = i;
    this.shoes.forEach((s, n) => s.classList.toggle('is-front', n === i));
    this.sheets.forEach((s, n) => (s.hidden = n !== i));
  }

  go(target) {
    this.target = target;
    if (this.raf) return;
    const tick = () => {
      const d = this.target - this.pos;
      this.pos += d * (reduceMotion ? 1 : 0.12);
      if (Math.abs(d) < 0.002) this.pos = this.target;
      this.render();
      this.raf = this.pos === this.target ? null : requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  drag() {
    let x0 = 0;
    let p0 = 0;
    let lastX = 0;
    let lastT = 0;
    let vel = 0;
    this.moved = false;
    this.stage.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      this.dragging = true;
      this.moved = false;
      x0 = lastX = e.clientX;
      lastT = performance.now();
      p0 = this.pos;
      vel = 0;
      cancelAnimationFrame(this.raf);
      this.raf = null;
    });
    window.addEventListener('pointermove', (e) => {
      if (!this.dragging) return;
      const dx = e.clientX - x0;
      if (Math.abs(dx) > 6) {
        this.moved = true;
        this.stage.classList.add('is-dragging');
      }
      if (!this.moved) return;
      const now = performance.now();
      vel = (e.clientX - lastX) / Math.max(1, now - lastT);
      lastX = e.clientX;
      lastT = now;
      this.pos = p0 - dx / Math.max(140, this.stage.clientWidth * 0.22);
      this.target = this.pos;
      this.render();
    });
    const end = () => {
      if (!this.dragging) return;
      this.dragging = false;
      this.stage.classList.remove('is-dragging');
      this.idleUntil = Date.now() + 8000;
      if (this.moved) this.go(Math.round(this.pos - vel * 2.2));
    };
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  }

  onClick(e) {
    const shoe = e.target.closest('.st-orbit__shoe');
    const size = e.target.closest('.st-orbit__size');
    const add = e.target.closest('[data-add]');
    const sheet = e.target.closest('[data-sheet]');
    if (shoe) {
      // Arrastrar nunca abre el producto; tocar uno de atrás lo trae al frente.
      const i = Number(shoe.dataset.i);
      if (this.moved || i !== this.current) {
        e.preventDefault();
        if (!this.moved) this.go(this.target + this.wrap(i - this.target));
        this.idleUntil = Date.now() + 8000;
      }
      this.moved = false;
      return;
    }
    if (size && !size.disabled) {
      sheet.querySelectorAll('.st-orbit__size').forEach((b) => b.classList.toggle('is-on', b === size));
      const m = size.title.match(/USA\s+(\d+)\s*EUR\s+(\d+)\s*COL/);
      const eur = sheet.querySelector('[data-eur]');
      if (eur) eur.textContent = m ? `EUR ${m[1]} · COL ${m[2]}` : size.title;
      const money = (c) => '$' + Math.round(c / 100).toLocaleString('es-CO');
      const btn = sheet.querySelector('[data-add]');
      btn.disabled = false;
      btn.textContent = `Agregar al carrito · ${money(sheet.querySelector('[data-price]').dataset.price)}`;
      this.idleUntil = Date.now() + 15000;
      return;
    }
    if (add && !add.disabled) this.add(sheet, add);
  }

  async add(sheet, button) {
    if (button.classList.contains('is-loading')) return;
    const id = Number(sheet.querySelector('.st-orbit__size.is-on').dataset.variantId);
    button.classList.add('is-loading');
    const img = this.shoes[this.current].querySelector('img');
    const flight = stFlyToBag(img.getBoundingClientRect(), img.currentSrc || img.src);
    try {
      await stAddToCart(id, this);
      await flight;
      button.classList.add('is-added');
      setTimeout(() => button.classList.remove('is-added'), 1200);
    } catch (error) {
      stToastError(error.message);
    } finally {
      button.classList.remove('is-loading');
    }
  }
}
if (!customElements.get('st-orbit')) customElements.define('st-orbit', StOrbit);

/* ==========================================================================
   8. Manifiesto (palabra por palabra) + Índice de colecciones (foto que sigue el cursor)
   ========================================================================== */
// Un solo "escuchador" de scroll para todo lo que depende de la posición
const stScrollers = new Set();
let stScrollRaf;
document.addEventListener(
  'scroll',
  () => {
    cancelAnimationFrame(stScrollRaf);
    stScrollRaf = requestAnimationFrame(() => stScrollers.forEach((fn) => fn()));
  },
  { capture: true, passive: true }
);
window.addEventListener('resize', () => stScrollers.forEach((fn) => fn()));

class StManifesto extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';
    const box = this.querySelector('[data-st-m-text]');
    this.progressEl = this.querySelector('[data-st-m-progress]');
    this.sign = this.querySelector('[data-st-m-sign]');
    // Envolver cada palabra (respetando negritas)
    const walker = document.createTreeWalker(box, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    for (const node of nodes) {
      const parts = node.textContent.split(/(\s+)/);
      const frag = document.createDocumentFragment();
      for (const part of parts) {
        if (!part) continue;
        if (/^\s+$/.test(part)) frag.appendChild(document.createTextNode(part));
        else {
          const s = document.createElement('span');
          s.className = 'st-word';
          s.textContent = part;
          frag.appendChild(s);
        }
      }
      node.replaceWith(frag);
    }
    this.words = [...box.querySelectorAll('.st-word')];
    this.lit = -1;
    if (reduceMotion || designMode) {
      this.words.forEach((w) => w.classList.add('is-lit'));
      this.sign?.classList.add('is-lit');
      return;
    }
    this.update = this.update.bind(this);
    stScrollers.add(this.update);
    this.update();
  }

  disconnectedCallback() {
    stScrollers.delete(this.update);
  }

  update() {
    const r = this.getBoundingClientRect();
    const total = Math.max(1, r.height - window.innerHeight);
    const p = Math.min(1, Math.max(0, (-r.top + window.innerHeight * 0.15) / total));
    const n = Math.round(p * this.words.length);
    if (n === this.lit) return;
    this.lit = n;
    this.words.forEach((w, i) => w.classList.toggle('is-lit', i < n));
    // Una frase en negrita se subraya completa cuando su última palabra se ilumina
    this.querySelectorAll('[data-st-m-text] strong, [data-st-m-text] b').forEach((el) => {
      const ws = el.querySelectorAll('.st-word');
      el.classList.toggle('is-done', ws.length > 0 && ws[ws.length - 1].classList.contains('is-lit'));
    });
    if (this.progressEl) this.progressEl.textContent = `${String(Math.round(p * 100)).padStart(2, '0')}%`;
    this.sign?.classList.toggle('is-lit', p >= 0.98);
  }
}
if (!customElements.get('st-manifesto')) customElements.define('st-manifesto', StManifesto);

class StCollectionIndex extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';
    this.giant = this.querySelector('[data-st-ci-giant] span');
    this.float = this.querySelector('[data-st-ci-float]');
    this.img = this.querySelector('[data-st-ci-img]');
    this.list = this.querySelector('.st-cindex__list');

    // Título gigante que se desliza con el scroll
    this.update = () => {
      if (!this.giant || reduceMotion) return;
      const r = this.getBoundingClientRect();
      const p = (window.innerHeight - r.top) / (window.innerHeight + r.height);
      const shift = (0.5 - Math.min(1, Math.max(0, p))) * window.innerWidth * 0.18;
      this.giant.style.setProperty('--st-ci-shift', `${shift.toFixed(1)}px`);
    };
    stScrollers.add(this.update);
    this.update();

    if (!finePointer || !this.float) return;
    let tx = 0;
    let ty = 0;
    let x = 0;
    let y = 0;
    let running = false;
    const fw = 280;
    const fh = 360;
    const loop = () => {
      const dx = tx - x;
      x += dx * 0.14;
      y += (ty - y) * 0.14;
      const rot = Math.max(-12, Math.min(12, dx * 0.08));
      this.float.style.setProperty('--x', `${x - fw / 2}px`);
      this.float.style.setProperty('--y', `${y - fh / 2}px`);
      this.float.style.setProperty('--r', `${rot.toFixed(2)}deg`);
      this.float.style.setProperty('--sc', this.classList.contains('is-hovering') ? '1' : '0.6');
      if (running) requestAnimationFrame(loop);
    };

    this.list.addEventListener('pointerenter', (e) => {
      x = tx = e.clientX;
      y = ty = e.clientY;
      running = true;
      requestAnimationFrame(loop);
    });
    this.list.addEventListener('pointermove', (e) => {
      tx = e.clientX;
      ty = e.clientY;
      const link = e.target.closest('.st-cindex__link');
      if (!link || link === this.current) return;
      this.current = link;
      if (link.dataset.img) {
        this.img.style.backgroundImage = `url("${link.dataset.img}")`;
        this.img.classList.remove('is-swap');
        void this.img.offsetWidth;
        this.img.classList.add('is-swap');
        this.classList.add('is-hovering');
      } else {
        this.classList.remove('is-hovering');
      }
    });
    this.list.addEventListener('pointerleave', () => {
      this.classList.remove('is-hovering');
      this.current = null;
      setTimeout(() => {
        if (!this.classList.contains('is-hovering')) running = false;
      }, 400);
    });
  }

  disconnectedCallback() {
    stScrollers.delete(this.update);
  }
}
if (!customElements.get('st-collection-index')) customElements.define('st-collection-index', StCollectionIndex);

/* ==========================================================================
   9. Videos que no están arriba: se descargan solo cuando la persona llega
   ========================================================================== */
function stLazyVideos() {
  const vids = document.querySelectorAll('video[data-st-lazy-video]');
  if (!vids.length) return;
  // La imagen previa también espera: se pone cuando el video está cerca
  const near = new IntersectionObserver(
    (entries) => {
      for (const { target, isIntersecting } of entries) {
        if (!isIntersecting) continue;
        if (target.dataset.stPoster) target.poster = target.dataset.stPoster;
        near.unobserve(target);
      }
    },
    { rootMargin: '900px 0px' }
  );
  vids.forEach((v) => near.observe(v));
  const io = new IntersectionObserver(
    (entries) => {
      for (const { target, isIntersecting } of entries) {
        if (isIntersecting) {
          if (!target.offsetWidth && !target.offsetHeight) continue;
          stVideoSrc(target);
          if (target.preload !== 'auto') target.preload = 'auto';
          if (!reduceMotion) target.play().catch(() => {});
        } else {
          target.pause();
        }
      }
    },
    { rootMargin: '300px 0px' }
  );
  vids.forEach((v) => io.observe(v));
}

/* ==========================================================================
   10. Fecha estimada de entrega en la página de producto (días hábiles, lun–vie)
   ========================================================================== */
function stDeliveryEstimate() {
  const el = document.querySelector('[data-st-delivery]');
  if (!el) return;
  const addBusinessDays = (date, n) => {
    const d = new Date(date);
    let added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    return d;
  };
  const now = new Date();
  const from = addBusinessDays(now, Number(el.dataset.min || 3));
  const to = addBusinessDays(now, Number(el.dataset.max || 5));
  const fmt = (d, withMonth) =>
    d.toLocaleDateString('es-CO', { weekday: 'short', day: 'numeric', ...(withMonth ? { month: 'short' } : {}) }).replace('.', '').replace(',', '');
  const sameMonth = from.getMonth() === to.getMonth();
  const range = el.querySelector('[data-st-delivery-range]');
  if (range) range.textContent = `${fmt(from, !sameMonth)} y el ${fmt(to, true)}`;
}

/* ==========================================================================
   11. Barra de progreso del scroll (arriba del todo)
   ========================================================================== */
function stProgress() {
  if (reduceMotion || designMode) return;
  const bar = document.createElement('div');
  bar.className = 'st-progress';
  bar.setAttribute('aria-hidden', 'true');
  document.body.appendChild(bar);
  const update = () => {
    const w = document.querySelector('.page-wrapper');
    const sc = w && w.scrollHeight > w.clientHeight + 20 ? w : document.scrollingElement;
    const max = sc.scrollHeight - sc.clientHeight;
    bar.style.setProperty('--p', max > 0 ? (sc.scrollTop / max).toFixed(4) : 0);
  };
  stScrollers.add(update);
  update();
}

/* ==========================================================================
   12. Efectos finos: brillo del estampado, transición catálogo→producto, precarga
   ========================================================================== */
function stShine() {
  if (reduceMotion || designMode) return;
  const add = (g) => {
    if (g.querySelector('.st-shine')) return;
    const s = document.createElement('span');
    s.className = 'st-shine';
    s.setAttribute('aria-hidden', 'true');
    g.appendChild(s);
  };
  const io = !finePointer
    ? new IntersectionObserver(
        (es) =>
          es.forEach((e) => {
            if (!e.isIntersecting) return;
            setTimeout(() => e.target.classList.add('st-shine-go'), 250);
            io.unobserve(e.target);
          }),
        { threshold: 0.6 }
      )
    : null;
  const scan = () =>
    document.querySelectorAll('.product-card .card-gallery:not([data-st-shine])').forEach((g) => {
      g.dataset.stShine = '1';
      add(g);
      if (io) io.observe(g);
    });
  scan();
  new MutationObserver(() => requestAnimationFrame(scan)).observe(document.querySelector('main') || document.body, { childList: true, subtree: true });

  if (!finePointer) return;
  document.addEventListener('pointermove', (e) => {
    const g = e.target instanceof Element && e.target.closest('.product-card .card-gallery');
    if (!g) return;
    const r = g.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    g.style.setProperty('--mx', `${(x * 100).toFixed(1)}%`);
    g.style.setProperty('--my', `${(y * 100).toFixed(1)}%`);
    g.style.setProperty('--ry', `${((x - 0.5) * 12).toFixed(2)}deg`);
    g.style.setProperty('--rx', `${((0.5 - y) * 12).toFixed(2)}deg`);
  });
  document.addEventListener(
    'pointerout',
    (e) => {
      const g = e.target instanceof Element && e.target.closest('.product-card .card-gallery');
      if (g && !g.contains(e.relatedTarget)) {
        g.style.setProperty('--rx', '0deg');
        g.style.setProperty('--ry', '0deg');
      }
    },
    true
  );
}

// La foto de la tarjeta "viaja" a la foto grande del producto (View Transitions entre páginas)
function stProductMorph() {
  if (reduceMotion || designMode) return;
  document.addEventListener('click', (e) => {
    const a = e.target instanceof Element && e.target.closest('.product-card a[href*="/products/"]');
    if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey) return;
    const img = a.closest('.product-card')?.querySelector('.card-gallery img');
    document.querySelectorAll('[style*="st-prenda"]').forEach((el) => (el.style.viewTransitionName = ''));
    if (img) img.style.viewTransitionName = 'st-prenda';
  });
  // En la página del producto, la primera foto recibe el mismo nombre para que el navegador las una
  const hero = document.querySelector('.product-information .product-media__image, .product-information img');
  if (hero) hero.style.viewTransitionName = 'st-prenda';
  // Volver atrás al catálogo: limpiar nombres repetidos
  window.addEventListener('pageshow', () => document.querySelectorAll('.card-gallery img').forEach((i) => (i.style.viewTransitionName = '')));
}

// Precarga: al posar el mouse o el dedo sobre una prenda, su página empieza a cargar
function stPrefetch() {
  const done = new Set();
  const go = (e) => {
    const a = e.target instanceof Element && e.target.closest('a[href*="/products/"]');
    if (!a || done.has(a.href) || done.size > 12) return;
    if (navigator.connection && (navigator.connection.saveData || /2g/.test(navigator.connection.effectiveType || ''))) return;
    done.add(a.href);
    const l = document.createElement('link');
    l.rel = 'prefetch';
    l.href = a.href;
    document.head.appendChild(l);
  };
  document.addEventListener('pointerover', go, { passive: true });
  document.addEventListener('touchstart', go, { passive: true });
}

/* ==========================================================================
   13. Ideas Undergold: barra de categorías, Frente/Espalda, ¿Cuál es mi talla?
   ========================================================================== */
function stDock() {
  if (!document.querySelector('.st-dock')) return;
  let last = 0;
  stScrollers.add(() => {
    const w = document.querySelector('.page-wrapper');
    const y = Math.max(window.scrollY, w ? w.scrollTop : 0);
    document.documentElement.classList.toggle('st-dock-hide', y > last + 4 && y > 300);
    if (y < last - 4) document.documentElement.classList.remove('st-dock-hide');
    last = y;
  });
}
function stFrontBack() {
  const box = document.querySelector('.st-view');
  if (!box) return;
  let side = 0;
  const apply = () =>
    document.querySelectorAll('.product-card slideshow-component').forEach((s) => {
      if (typeof s.select === 'function') s.select(side, undefined, { animate: true }).catch?.(() => {});
    });
  box.addEventListener('click', (e) => {
    const b = e.target.closest('[data-st-view]');
    if (!b) return;
    side = Number(b.dataset.stView);
    box.querySelectorAll('button').forEach((x) => x.classList.toggle('is-on', x === b));
    apply();
  });
  new MutationObserver(() => side && requestAnimationFrame(apply)).observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
}
// Medidas reales de la guía: semicontorno de pecho de la prenda (cm)
const ST_PECHO = [['S', 54], ['M', 56], ['L', 57], ['XL', 59], ['XXL', 61]];
function stSizeFinder() {
  const input = document.querySelector('[data-st-sf-input]');
  const out = document.querySelector('[data-st-sf-out]');
  if (!input || !out) return;
  input.addEventListener('input', () => {
    const v = parseFloat(String(input.value).replace(',', '.'));
    if (!v || v < 40) { out.textContent = ''; return; }
    let rec = ST_PECHO.find(([, c]) => c >= v - 0.5);
    let extra = '';
    if (!rec) { rec = ST_PECHO[ST_PECHO.length - 1]; extra = ' (te quedará un poco más ajustada; escríbenos y te asesoramos)'; }
    out.innerHTML = '';
    const s = document.createElement('span'); s.innerHTML = 'Tu talla: <strong></strong>';
    s.querySelector('strong').textContent = rec[0];
    out.append(s, document.createTextNode(` · la prenda mide ${rec[1]} cm de axila a axila${extra}`));
    // Selecciona esa talla en la página si existe
    const lbl = [...document.querySelectorAll('.product-information label')].find((l) => l.textContent.trim() === rec[0]);
    if (lbl) lbl.click();
  });
}

/* ==========================================================================
   14. Rastro de prendas · Túnel 3D · Abanico de colección
   ========================================================================== */
class StTrail extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';
    let urls = [];
    try { urls = JSON.parse(this.dataset.images || '[]').filter(Boolean); } catch (e) {}
    if (!urls.length || reduceMotion) return;
    this.pool = [];
    this.idx = 0;
    this.last = null;
    for (let i = 0; i < 14; i++) {
      const img = document.createElement('img');
      img.className = 'st-trail__img';
      img.alt = '';
      img.decoding = 'async';
      img.style.opacity = '0';
      this.appendChild(img);
      this.pool.push(img);
    }
    this.urls = [];
    const pre = () => urls.forEach((u) => { const im = new Image(); im.decoding = 'async'; im.onload = () => this.urls.push(u); im.src = u; });
    new IntersectionObserver(([en], ob) => { if (en.isIntersecting) { pre(); ob.disconnect(); } }, { rootMargin: '600px 0px' }).observe(this);
    const move = (x, y) => {
      const r = this.getBoundingClientRect();
      const px = x - r.left, py = y - r.top;
      if (this.last && Math.hypot(px - this.last[0], py - this.last[1]) < 90) return;
      this.last = [px, py];
      this.spawn(px, py);
    };
    this.addEventListener('pointermove', (e) => move(e.clientX, e.clientY));
    this.addEventListener('touchmove', (e) => { const t = e.touches[0]; if (t) move(t.clientX, t.clientY); }, { passive: true });
    // Sin mouse (celular): las prendas salen solas mientras la sección está a la vista
    if (!finePointer) {
      let t = 0, timer = null;
      new IntersectionObserver(([en]) => {
        clearInterval(timer);
        if (!en.isIntersecting) return;
        timer = setInterval(() => {
          t += 0.55;
          const w = this.clientWidth, h = this.clientHeight;
          this.spawn(w / 2 + Math.sin(t) * w * 0.34, h / 2 + Math.sin(t * 1.7) * h * 0.28);
        }, 380);
      }, { threshold: 0.3 }).observe(this);
    }
  }
  spawn(x, y) {
    if (!this.urls.length) return;
    const img = this.pool[this.idx % this.pool.length];
    const url = this.urls[this.idx % this.urls.length];
    this.idx++;
    if (img.getAttribute('src') !== url) img.src = url;
    const w = img.offsetWidth || 180, h = w * 4 / 3;
    const rot = (Math.random() - 0.5) * 18;
    img.getAnimations().forEach((a) => a.cancel());
    img.style.zIndex = String(this.idx);
    img.animate(
      [
        { transform: `translate(${x - w / 2}px, ${y - h / 2}px) scale(0.6) rotate(${rot}deg)`, opacity: 0 },
        { transform: `translate(${x - w / 2}px, ${y - h / 2}px) scale(1) rotate(${rot}deg)`, opacity: 1, offset: 0.18 },
        { transform: `translate(${x - w / 2}px, ${y - h / 2}px) scale(1) rotate(${rot}deg)`, opacity: 1, offset: 0.7 },
        { transform: `translate(${x - w / 2}px, ${y - h / 2 + 40}px) scale(0.85) rotate(${rot}deg)`, opacity: 0 },
      ],
      { duration: 1500, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' }
    );
  }
}
if (!customElements.get('st-trail')) customElements.define('st-trail', StTrail);

class StTunnel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';
    this.items = [...this.querySelectorAll('.st-tunnel__item')];
    if (!this.items.length) return;
    new IntersectionObserver(([en], ob) => {
      if (!en.isIntersecting) return;
      this.querySelectorAll('img[data-src]').forEach((im) => { im.src = im.dataset.src; im.removeAttribute('data-src'); });
      ob.disconnect();
    }, { rootMargin: '900px 0px' }).observe(this);
    const n = this.items.length;
    // Posiciones fijas alrededor del centro, a distintas profundidades
    this.pos = this.items.map((_, i) => {
      const ang = i * 2.399; // ángulo áureo: reparte sin amontonar
      const rad = 0.55 + (i % 3) * 0.18;
      return { x: Math.cos(ang) * rad, y: Math.sin(ang) * rad * 0.62, z: -i * (3200 / n) };
    });
    this.update = () => {
      const r = this.getBoundingClientRect();
      const total = Math.max(1, r.height - window.innerHeight);
      const p = Math.min(1, Math.max(0, -r.top / total));
      const W = window.innerWidth, H = window.innerHeight;
      this.items.forEach((el, i) => {
        const o = this.pos[i];
        const z = o.z + p * 3800;
        const op = z > 700 ? 0 : z > 350 ? 1 - (z - 350) / 350 : z < -2600 ? Math.max(0, 1 + (z + 2600) / 600) : 1;
        el.style.opacity = op.toFixed(3);
        el.style.transform = `translate3d(${(o.x * W * 0.5).toFixed(1)}px, ${(o.y * H * 0.5).toFixed(1)}px, ${z.toFixed(1)}px)`;
        el.style.visibility = op <= 0.01 ? 'hidden' : 'visible';
      });
    };
    if (reduceMotion) { this.items.forEach((el) => (el.style.opacity = '0.35')); return; }
    stScrollers.add(this.update);
    this.update();
  }
  disconnectedCallback() { stScrollers.delete(this.update); }
}
if (!customElements.get('st-tunnel')) customElements.define('st-tunnel', StTunnel);

class StFan extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';
    requestAnimationFrame(() => setTimeout(() => this.classList.add('is-open'), 120));
    if (!finePointer || reduceMotion) return;
    const cards = [...this.querySelectorAll('.st-fan__card')];
    this.addEventListener('pointermove', (e) => {
      const r = this.getBoundingClientRect();
      const dx = (e.clientX - r.left) / r.width - 0.5, dy = (e.clientY - r.top) / r.height - 0.5;
      cards.forEach((c, i) => {
        const depth = 1 + Math.abs(i - 2) * 0.6;
        c.style.setProperty('--px', `${(dx * 40 * depth).toFixed(1)}px`);
        c.style.setProperty('--py', `${(dy * 26 * depth).toFixed(1)}px`);
      });
    });
    this.addEventListener('pointerleave', () => cards.forEach((c) => { c.style.setProperty('--px', '0px'); c.style.setProperty('--py', '0px'); }));
  }
}
if (!customElements.get('st-fan')) customElements.define('st-fan', StFan);

/* ==========================================================================
   15. Banner que se revela (linterna). Solo cambia 3 variables CSS: nada pesa.
   ========================================================================== */
class StLamp extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';
    if (reduceMotion) return;
    let tx = 50, ty = 45, x = 50, y = 45, r = 0, tr = 0, raf = null, auto = !finePointer, t = 0;
    const R = () => Math.max(150, Math.min(this.clientWidth, this.clientHeight) * 0.32);
    const loop = () => {
      if (auto) { t += 0.012; tx = 50 + Math.sin(t) * 30; ty = 48 + Math.sin(t * 1.6) * 18; tr = R(); }
      x += (tx - x) * 0.12; y += (ty - y) * 0.12; r += (tr - r) * 0.1;
      this.style.setProperty('--x', x.toFixed(2) + '%');
      this.style.setProperty('--y', y.toFixed(2) + '%');
      this.style.setProperty('--r', r.toFixed(1) + 'px');
      raf = requestAnimationFrame(loop);
    };
    const set = (cx, cy) => { const b = this.getBoundingClientRect(); tx = ((cx - b.left) / b.width) * 100; ty = ((cy - b.top) / b.height) * 100; tr = R(); };
    this.addEventListener('pointermove', (e) => { auto = false; set(e.clientX, e.clientY); });
    this.addEventListener('pointerleave', () => { tr = 0; if (!finePointer) auto = true; });
    this.addEventListener('touchmove', (e) => { const p = e.touches[0]; if (p) { auto = false; set(p.clientX, p.clientY); } }, { passive: true });
    this.addEventListener('touchend', () => { auto = true; });
    // Solo anima mientras se ve en pantalla
    new IntersectionObserver(([en]) => {
      if (en.isIntersecting && !raf) loop();
      else if (!en.isIntersecting && raf) { cancelAnimationFrame(raf); raf = null; }
    }).observe(this);
  }
}
if (!customElements.get('st-lamp')) customElements.define('st-lamp', StLamp);

/* ==========================================================================
   16. Videos: solo se descarga el que se ve, en la calidad justa para la pantalla
   ========================================================================== */
const stIsMobile = () => window.matchMedia('(max-width: 749px)').matches;
function stVideoSrc(v) {
  if (v.src) return;
  // Elige la versión más liviana que se vea bien: celular desde 480p, computador desde 720p
  const list = String(v.dataset.stSources || '')
    .split(',')
    .map((x) => { const i = x.indexOf('|'); return { h: parseInt(x.slice(0, i), 10), url: x.slice(i + 1) }; })
    .filter((x) => x.h && x.url)
    .sort((a, b) => a.h - b.h);
  const min = stIsMobile() ? 480 : 720;
  const pick = list.find((x) => x.h >= min) || list[list.length - 1];
  v.src = (pick && pick.url) || (stIsMobile() ? v.dataset.srcM : v.dataset.srcD) || v.dataset.srcM || '';
}
function stHeroVideos() {
  document.querySelectorAll('video[data-st-hero-video]').forEach((v) => {
    // El otro (computador o celular) está oculto con CSS: no se descarga
    if (!v.offsetWidth && !v.offsetHeight) return;
    stVideoSrc(v);
    v.preload = 'auto';
    if (!reduceMotion) v.play().catch(() => {});
  });
}

// Primero lo que sirve para comprar; lo decorativo cuando el navegador quede libre
const stInit = () => {
  stIntro();
  stListenCart();
  stHeroVideos();
  stLazyVideos();
  stSizeFinder();
  stDeliveryEstimate();
  stPrefetch();
  stWelcome();
  const decor = () => {
    stReveal();
    stShine();
    stProductMorph();
    stCursor();
    stMarqueeSkew();
    stMagnetic();
    stProgress();
    stDock();
    stFrontBack();
  };
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1));
  const later = () => idle(decor, { timeout: 2500 });
  if (document.readyState === 'complete') later();
  else window.addEventListener('load', later, { once: true });
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', stInit);
else stInit();
